import { SupplierModelType } from '@teinor/erp/types/company/supplier'
import { SupplierServiceModelType } from '@teinor/erp/types/company/supplier/supplierService'
import React, { useCallback, useEffect, useState } from 'react'
import { ComponentWithPermissions } from '../../../../../../../../baseComponents/CreateRoutering/types'
import { dictionaryComplexFromJsonArr } from '../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import ModalCECreditorServiceConnect from './ModalCECreditorService'
import TwinTable from '../../../../../../../../baseComponents/TwinTable'
import BaseScreenTableInner from '../../../../../../../../baseComponents/TwinTable/BaseScreenTableInner'
import { TableComponentBaseScreenInnerProps } from '../../../../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderString, EditRow } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { DictionaryCreditorServiceType } from './types'
import LoadingSpinner from '../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import DisplayPrice from '../../../../../../../../baseComponents/Displays/DisplayPrice'

interface CreditorServicesProps extends ComponentWithPermissions {
    basicData: SupplierModelType | null
}

const CreditorServices: React.FC<CreditorServicesProps> = ({ basicData, userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('services', 'Servicios'),
        newText: t('addServices', 'Nuevo Servicio'),
    }

    return (
        <BaseScreenTableInner TableComponent={TableCreditorServices} CreateModalComponent={ModalCECreditorServiceConnect} EditModalComponent={ModalCECreditorServiceConnect} pageData={pageData} userPermissions={userPermissions} extraWhereParams={{ SupplierId: basicData?.id }} />
    )
}

interface TableCreditorServicesProps extends TableComponentBaseScreenInnerProps  {}


const TableCreditorServices: React.FC<TableCreditorServicesProps> = ({ setTableInstance, extraWhereParams, setOpened }) => {
    const { t } = useTwinTranslation()
    const {serviceTypes } = useTableCreditorServicesLogic()
    if (!serviceTypes) {
        return <LoadingSpinner />
    }
    const columns: ColumnTableSchema<SupplierServiceModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            sortable: true,
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            minWidth: 130,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'unitPrice',
            dataKey: 'unitPrice',
            label: t('unitPrice', 'Precio Unitario'),
            minWidth: 120,
            customRender: (parameterValue, allRowData) =>
                <DisplayPrice value={parseFloat(parameterValue || '0')} CurrencyId={allRowData?.CurrencyId || 'EUR'} with_vat={false} calculateParams={{
                    decimals: 2,
                    rounding: '0.01',
                    roundingPriceType: 'medium'
                }} />
        },
        {
            id: 'ServiceTypeId',
            dataKey: 'ServiceTypeId',
            label: t('expenseType', 'Tipo de gasto'),
            minWidth: 110,
            customRender: (parameterValue) => <CustomRenderString value={serviceTypes?.[parseInt(parameterValue)]?.name || ''} />,
        },
        {
            id: 'BillingRetentionId',
            dataKey: 'BillingRetentionId',
            label: t('retentionType', 'Tipo de retención'),
            className: 'twin_elipsis',
            customRender: (parameterValue, allRowData) => <CustomRenderString value={(allRowData?.BillingRetention?.code|| '') + ' - ' + (allRowData?.BillingRetention?.name|| '')} />,
            hideColumnByDefault: true
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        }
    ]
    return (
        <TwinTable columns={columns} name='CreditorServices' getDataFrom='/api/app/supplier/service/getAllSupplierServices'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData
            })}
            setTableInstance={setTableInstance} extraWhereParams={extraWhereParams}
        />
    )
}

const useTableCreditorServicesLogic = () => {
    const [serviceTypes, setServiceTypes] = useState<DictionaryCreditorServiceType | null>(null)
    const getServiceTypes = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/config/serviceType/getAllServiceTypes', {})
        if (result) {
            setServiceTypes(dictionaryComplexFromJsonArr(result))
        }
    }, [setServiceTypes])

    useEffect(() => {
        getServiceTypes()
    }, [getServiceTypes])
    return { serviceTypes }
}

export default CreditorServices